import { createSelector } from '@reduxjs/toolkit';
import {
  filter,
  map,
  pipe,
  sortBy,
  uniqueBy,
  allPass,
  join,
  isTruthy,
} from 'remeda';
import { type RootState } from '@/store';
import { getIconBySportId } from '@/utils';

export const dailyMatches = ({ matches: { dailyMatches } }: RootState) =>
  dailyMatches;

export const selectedDate = ({ matches: { selectedDate } }: RootState) =>
  selectedDate;

export const selectedSport = ({ matches: { selectedSport } }: RootState) =>
  selectedSport;

export const hasChanges = ({
  matches: {
    editedMatches,
    editedCompetitions,
    addLiveBlog,
    addHomeWidget,
    syncMatchData,
  },
}: RootState) =>
  editedMatches.length ||
  editedCompetitions.length ||
  addLiveBlog.length ||
  addHomeWidget.length ||
  syncMatchData.length;

export const selectedCompetition = ({
  matches: { selectedCompetition },
}: RootState) => selectedCompetition;

export const currentDailyMatches = createSelector(
  dailyMatches,
  selectedDate,
  (dailyMatches, selectedDate) => dailyMatches[selectedDate || ''],
);

export const filteredDailyMatches = createSelector(
  currentDailyMatches,
  selectedSport,
  selectedCompetition,
  (currentDailyMatches, selectedSport, selectedCompetition) =>
    currentDailyMatches?.filter((match) =>
      allPass(match, [
        (match) =>
          selectedSport === 'all'
            ? true
            : match?.sport?.external_id === selectedSport,
        (match) =>
          selectedCompetition === ''
            ? true
            : match?.competition?.external_id === selectedCompetition,
      ]),
    ),
);

export const groupedDailyMatches = createSelector(
  filteredDailyMatches,
  (filteredDailyMatches) => {
    if (!filteredDailyMatches) return undefined;

    const competitions = pipe(
      filteredDailyMatches,
      map(({ competition, category }) => ({
        ...competition,
        id: competition.external_id,
        category_name: category?.name,
        edit: {
          name: competition.edit?.name,
          category_name: category.edit?.name,
        },
        matches: filter(
          filteredDailyMatches,
          ({ competition: { external_id: competitionExternalId } }) =>
            competitionExternalId === competition.external_id,
        ),
      })),
      uniqueBy(({ external_id }) => external_id),
      sortBy(({ ordinal }) => ordinal ?? Infinity),
    );

    return competitions;
  },
);

export const competitions = createSelector(
  currentDailyMatches,
  selectedSport,
  (currentDailyMatches, selectedSport) => {
    if (!currentDailyMatches) return [];

    return pipe(
      currentDailyMatches,
      filter((match) =>
        selectedSport === 'all'
          ? true
          : match?.sport?.external_id === selectedSport,
      ),
      map(({ category, competition, sport }) => {
        const name = pipe(
          [category?.name, competition?.name],
          filter(isTruthy),
          join(' | '),
        );

        return {
          id: competition.external_id,
          name,
          sportId: sport.external_id,
          ordinal: competition.ordinal,
          Icon: getIconBySportId(sport.external_id),
        };
      }),
      uniqueBy(({ id }) => id),
      sortBy(({ ordinal }) => ordinal ?? Infinity),
    );
  },
);
